import { assign } from 'lodash';
import React, { memo, useCallback, useRef, useState } from 'react';
import {
  Dimensions,
  Platform,
  ScrollView,
  ScrollViewProps,
  View
} from 'react-native';
import styled from 'styled-components/native';

import Colors from '../../constants/Colors';
import { P } from '../Typography';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLabel,
  VictoryLine,
  VictoryScatter
} from './Victory';

const StyledView = styled.View`
  background: white;
  border-radius: 24px;
  padding: 2px 0; // no left/right padding due to VictoryChart width setting
  align-items: center;
  width: 100%;
  max-width: 100%;
`;

const StyledP = styled(P)`
  font-size: 10px;
  line-height: 11.73px;
  flex-shrink: 1;
`;

const StyledDayText = styled(P)`
  font-size: 10px;
  line-height: 11.73px;
  flex-shrink: 1;
`;

const StyledLegendIcon = styled.View`
  border-radius: 14px;
  height: 14px;
  width: 14px;
  background-color: #5b4fc2;
  margin-bottom: 2px;
  margin-right: 10px;
`;

const StyledLegendItemContainter = styled.View`
  flex-shrink: 1;
  flex-direction: row;
  align-items: center;
  padding: 0px 20px 5px 20px;
`;

const StyledLegendsContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  padding: 5px 0px 10px 25px;
  width: 100%;
`;

const StyledLoadingIndicator = styled.ActivityIndicator`
  position: absolute;
  top: 0;
  bottom: 0;
`;

const StyledRowView = styled.View`
  height: 270px;
  padding-right: 24px;
  flex-direction: row;
`;

const StyledDayTextView = styled.View`
  width: 100%;
  align-items: center;
  margin-bottom: 20px;
`;

export type RollingAverageChartProps = {
  label: string;
  dataSets: Array<RollingAverageDataSet>;
  goal?: number;
  currentDay: number;
  length?: number;
};

export type RollingAverageDataSet = {
  caption?: string;
  chartData: Array<ChartData>;
};

export type ChartData = {
  group?: number;
  x: number;
  y: number;
};

const RollingAverageChart: React.FC<RollingAverageChartProps> = ({
  label = 'Axis',
  dataSets,
  goal,
  currentDay,
  length
}) => {
  const graphColors = ['#E6E3FE', '#E7FFDC'];
  const graphLineColors = ['#5B4FC2', '#1C9C7D'];
  const [layoutWidth, setLayoutWidth] = useState(0);
  // get the max day count
  const xAxisMaxValueLabel =
    length ||
    dataSets.reduce(
      (result, dataSet) =>
        Math.max(
          result,
          dataSet.chartData.reduce(
            (result, data) => Math.max(result, data.x),
            0
          )
        ),
      0
    ) ||
    10;
  const maxZooomDomain =
    xAxisMaxValueLabel > 21 && layoutWidth < 500
      ? 21
      : xAxisMaxValueLabel > 28 && layoutWidth < 750
      ? 28
      : xAxisMaxValueLabel;
  // Y-axis data Line Graph computation
  const yDataLineGraphManipulation = (data: any) => {
    const newArray = data.map((item: { x: number; y: number }) => ({
      x: item.x,
      y: item.y
    }));
    return newArray;
  };

  const getChartElementStyles = (index = 0) => {
    const xAxisTickStyle = {
      ticks: { padding: -2 },
      tickLabels: {
        fill: (tick: any) => (tick.text <= currentDay ? 'black' : '#dddddd'),
        fontSize: '7.65px',
        fontFamily: 'WorkSans-SemiBold',
        lineHeight: '8.97px',
        letterSpacing: '-0.24px'
      }
    };

    const xAxisStyle = assign({}, xAxisTickStyle, {
      grid: {
        stroke: index === 0 ? 'rgba(239, 242, 243, 0.3)' : 'transparent'
      },
      axis: { stroke: '#FAF6F5', strokeWidth: 0.5 },
      axisLabel: {
        fill: 'black',
        fontSize: '9px',
        fontFamily: 'WorkSans-Regular',
        lineHeight: '10.56px',
        letterSpacing: '-0.256px',
        padding: 30
      }
    });

    const xAxisDummyLineStyle = {
      axis: { stroke: '#FAF6F5' },
      ticks: { padding: -4 },
      tickLabels: {
        fill: 'black',
        fontSize: '12px',
        fontFamily: 'WorkSans-SemiBold'
      },
      grid: { stroke: 'transparent' }
    };

    const yAxisStyle = {
      grid: { stroke: Colors.lightText, strokeWidth: 0 },
      axis: { stroke: 'transparent', strokeWidth: 0.5 },
      axisLabel: {
        fill: 'black',
        fontSize: '8px',
        fontFamily: 'WorkSans-Regular',
        lineHeight: '9.38px',
        letterSpacing: '-0.26px',
        padding: 30
      },
      ticks: { padding: -6 },
      tickLabels: {
        fill: 'black',
        fontSize: '7.65px',
        fontFamily: 'WorkSans-SemiBold',
        lineHeight: '8.97px',
        letterSpacing: '-0.24px'
      }
    };

    const lineStyle = {
      data: {
        stroke: graphLineColors[index],
        strokeWidth: 4
      }
    };

    const circleStyle = {
      data: {
        fill: graphLineColors[index]
      }
    };

    return {
      xAxis: xAxisStyle,
      xAxisDummyLine: xAxisDummyLineStyle,
      yAxis: yAxisStyle,
      line: lineStyle,
      circle: circleStyle
    };
  };

  const DataLabel = (props: any) => {
    const y = props.scale.y(props.y);
    return <VictoryLabel {...props} x={props.x} y={y} />;
  };

  function getYAxisMinValue(): number {
    const emptyDataSets =
      dataSets.filter(data => data.chartData.length > 0).length < 1;
    if (emptyDataSets) return 0;
    let yAxisMinValue = Math.min(
      ...dataSets
        .filter(data => data.chartData.length > 0)
        .flatMap(data => data.chartData.flatMap(chartData => chartData.y))
    );
    if (goal) {
      yAxisMinValue = Math.min(yAxisMinValue, goal);
    }
    return yAxisMinValue - (getYAxisMaxValue() / 10) * 0.2;
  }

  function getYAxisMaxValue(): number {
    const emptyDataSets =
      dataSets.filter(data => data.chartData.length > 0).length < 1;
    if (emptyDataSets && !goal) return 8;
    let yAxisMaxValue = Math.max(
      ...dataSets
        .filter(data => data.chartData.length > 0)
        .flatMap(data => data.chartData.flatMap(chartData => chartData.y))
    );
    if (goal) {
      yAxisMaxValue = Math.max(yAxisMaxValue, goal);
    }
    return yAxisMaxValue + yAxisMaxValue * 0.05;
  }

  const sortedChartData = dataSets
    .flatMap((dataSet: RollingAverageDataSet, index: number) => {
      return dataSet.chartData.map((chrtData: ChartData) => {
        chrtData.group = index;
        return chrtData;
      });
    })
    .sort((a, b) => a.x - b.x);

  const renderGraph = () => {
    const victoryGroups: JSX.Element[] = [];
    const entriesGroup: Array<Array<ChartData>> = [];
    let entries: Array<ChartData> = [];
    const rollingAverageData: Array<ChartData> = [];

    sortedChartData.forEach((chartData, index) => {
      if (entries.length === 0) {
        entries.push(chartData);
      } else {
        if (chartData.group === entries[entries.length - 1].group) {
          entries.push(chartData);
        } else {
          entriesGroup.push(entries);
          entries = [];
          entries.push(chartData);
        }
      }

      if (entries.length > 0 && index + 1 >= sortedChartData.length) {
        entriesGroup.push(entries);
        entries = [];
      }

      const filteredEntries = entriesGroup
        .flatMap(data => data)
        .concat(entries)
        .filter(data => data.group === chartData.group);

      rollingAverageData.push({
        x: chartData.x,
        y:
          filteredEntries.reduce(
            (total: number, currentValue: ChartData) =>
              (total = total + currentValue.y),
            0
          ) / filteredEntries.length
      });
    });

    entriesGroup.forEach((entries, index) => {
      const entriesRollingAverageData: Array<ChartData> = [];
      for (const entry of entries) {
        entriesRollingAverageData.push(
          rollingAverageData.find(data => data.x === entry.x) || entry
        );
      }

      victoryGroups.push(
        <VictoryGroup key={index}>
          <VictoryArea
            style={{
              data: { fill: graphColors[entries[0].group || 0 % 2] }
            }}
            data={entriesRollingAverageData}
          />
          <VictoryLine
            key={index}
            data={entriesRollingAverageData}
            style={getChartElementStyles(entries[0].group || 0 % 2).line}
            labels={(data: { label: any }) => data.label}
          />
          <VictoryScatter
            key={index}
            size={4}
            data={entries}
            style={getChartElementStyles(entries[0].group || 0 % 2).circle}
            labels={(data: { label: any }) => data.label}
          />
        </VictoryGroup>
      );
    });
    return victoryGroups;
  };

  const screenWidth = Dimensions.get('window').width;
  const chartWidth = Math.max(screenWidth, xAxisMaxValueLabel * 30);

  // scrollview that allows drag scrolling for web
  const HorizontalDragScrollView: React.FC<
    { children: React.ReactNode } & ScrollViewProps
  > = ({ children, ...props }) => {
    if (Platform.OS !== 'web') {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          overScrollMode='never'
          {...props}
        >
          {children}
        </ScrollView>
      );
    }

    // on web use a div
    const divRef = useRef<HTMLDivElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      if (!divRef.current) return;
      setIsDragging(true);
      startXRef.current = e.pageX - divRef.current.offsetLeft;
      scrollLeftRef.current = divRef.current.scrollLeft;
    }, []);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (!isDragging || !divRef.current) return;
        e.preventDefault();
        const x = e.pageX - divRef.current.offsetLeft;
        const walk = x - startXRef.current;
        divRef.current.scrollLeft = scrollLeftRef.current - walk;
      },
      [isDragging]
    );

    const handleMouseUp = useCallback(() => setIsDragging(false), []);
    const handleMouseLeave = useCallback(() => setIsDragging(false), []);

    return (
      <div
        ref={divRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          scrollBehavior: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        className='hide-scrollbar'
      >
        {/* Keep children in a row and prevent flex shrinking */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexShrink: 0
          }}
        >
          {children}
        </div>

        {/* Hide scrollbar on webkit */}
        <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      </div>
    );
  };

  return (
    <StyledView
      onLayout={event => {
        const { width } = event.nativeEvent.layout;
        /* This is to prevent the updating of the size
         * even if the window size did not change
         */
        if (layoutWidth || width === Dimensions.get('window').width) return;
        setLayoutWidth(width);
      }}
    >
      {!layoutWidth && <StyledLoadingIndicator color={Colors.primary} />}
      <StyledRowView>
        <View>
          <VictoryChart
            height={270}
            width={62}
            padding={{ top: 25, bottom: 30 }}
            domain={{
              y: [
                dataSets.length > 0 ? getYAxisMinValue() : 0,
                dataSets.length > 0 ? getYAxisMaxValue() : goal || 8
              ],
              x: [1, xAxisMaxValueLabel]
            }}
          >
            <VictoryAxis
              crossAxis={false}
              label={label.toUpperCase()}
              dependentAxis
              offsetX={59}
              style={getChartElementStyles().yAxis}
              tickFormat={(tickValue: number) => tickValue}
              tickCount={11}
            />
          </VictoryChart>
        </View>
        <HorizontalDragScrollView>
          <VictoryChart
            height={270}
            width={chartWidth}
            padding={{ top: 25, bottom: 30, left: 8, right: 25 }}
            domain={{
              y: [
                dataSets.length > 0 ? getYAxisMinValue() : 0,
                dataSets.length > 0 ? getYAxisMaxValue() : goal || 8
              ],
              x: [1, xAxisMaxValueLabel]
            }}
          >
            {/* X-Axis */}
            <VictoryAxis
              offsetY={29.2}
              style={getChartElementStyles().xAxis}
              domain={{ x: [1, xAxisMaxValueLabel] }}
              tickCount={maxZooomDomain}
              tickFormat={(tickLabel: number) => tickLabel.toFixed(0)}
            />
            {/* Present Day Underline */}
            <VictoryAxis
              offsetY={29.2}
              style={getChartElementStyles().xAxisDummyLine}
              domain={{ x: [1, xAxisMaxValueLabel] }}
              tickCount={xAxisMaxValueLabel}
              tickFormat={(tickLabel: number) =>
                tickLabel === currentDay ? `_` : ``
              }
            />

            {/* Graph */}
            {renderGraph()}

            {/** Guide Line */}
            <VictoryLine
              y={() => goal || 0}
              style={{
                data: {
                  stroke: 'rgba(77, 67, 154, 0.81)',
                  strokeWidth: 1,
                  strokeDasharray: '3.69, 3.69',
                  opacity: goal ? 1 : 0
                }
              }}
            />
            <DataLabel
              textAnchor='middle'
              y={goal || 0}
              x={layoutWidth - 40}
              text='Your Goal'
              dy={1.8}
              lineHeight={2.2}
              style={{
                fill: 'black',
                fontSize: '8.31px',
                fontFamily: 'WorkSans-SemiBold',
                letterSpacing: '-0.29px',
                opacity: goal ? 1 : 0
              }}
              backgroundStyle={{
                fill: 'white',
                rx: 9,
                opacity: goal ? 1 : 0
              }}
              backgroundPadding={{
                top: -2.8,
                bottom: -1.8,
                left: 5,
                right: 6
              }}
            />
          </VictoryChart>
        </HorizontalDragScrollView>
      </StyledRowView>
      <StyledDayTextView>
        <StyledDayText color='dark'>DAY</StyledDayText>
      </StyledDayTextView>
      <StyledLegendsContainer>
        <StyledLegendItemContainter>
          <StyledLegendIcon />
          <StyledP color='dark'>
            {dataSets[0]?.caption || 'Without Medication'}
          </StyledP>
        </StyledLegendItemContainter>
        <StyledLegendItemContainter>
          <StyledLegendIcon style={{ backgroundColor: '#1C9C7D' }} />
          <StyledP color='dark'>
            {dataSets[1]?.caption || 'With medication'}
          </StyledP>
        </StyledLegendItemContainter>
      </StyledLegendsContainer>
    </StyledView>
  );
};

export default memo(RollingAverageChart);
